import type {
	IAMRole, UserClass,
	AppRegistryEntry, FeatureRegistryEntry,
	PermissionMap,
	RolePermission, SetRolePermissionsPayload,
	SetUserPermissionsPayload,
	AppAccessLevel, RoleAppLevel, SetRoleLevelsPayload,
} from '@appic/data';
import {
	mapRoleRow, mapAppRow, mapFeatureRow,
	mapPermissionGrant, mapRolePermission,
	mapAccessLevel, mapRoleAppLevel,
} from './iam.mappers';

async function sf() {
	const { serviceFetch } = await import('../http/serviceLayerClient');
	return serviceFetch;
}

// ── Roles ──

export async function listIAMRoles(): Promise<IAMRole[]> {
	const serviceFetch = await sf();
	const response = await serviceFetch('/v1/iam/roles', { method: 'GET' });
	if (response.status !== 200) {
		throw new Error(`Failed to fetch IAM roles: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && Array.isArray(result.data)) {
		return (result.data as Array<Record<string, unknown>>).map(mapRoleRow);
	}
	throw new Error('Unexpected response from GET /v1/iam/roles');
}

export async function createIAMRole(input: { id: string; label: string; userClass: UserClass }): Promise<IAMRole> {
	const serviceFetch = await sf();
	const response = await serviceFetch('/v1/iam/roles', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			id: input.id,
			label: input.label,
			user_class: input.userClass,
		}),
	});
	if (response.status !== 200 && response.status !== 201) {
		throw new Error(`Failed to create IAM role: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && result.data) {
		return mapRoleRow(result.data as Record<string, unknown>);
	}
	throw new Error('Unexpected response from POST /v1/iam/roles');
}

export async function updateIAMRole(id: string, updates: { label?: string; userClass?: UserClass }): Promise<IAMRole> {
	const serviceFetch = await sf();
	const payload: Record<string, unknown> = {};
	if (updates.label !== undefined) payload.label = updates.label;
	if (updates.userClass !== undefined) payload.user_class = updates.userClass;

	const response = await serviceFetch(`/v1/iam/roles/${id}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload),
	});
	if (response.status !== 200) {
		throw new Error(`Failed to update IAM role: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && result.data) {
		return mapRoleRow(result.data as Record<string, unknown>);
	}
	throw new Error('Unexpected response from PATCH /v1/iam/roles');
}

export async function deleteIAMRole(id: string): Promise<void> {
	const serviceFetch = await sf();
	const response = await serviceFetch(`/v1/iam/roles/${id}`, { method: 'DELETE' });
	if (response.status !== 200 && response.status !== 204) {
		throw new Error(`Failed to delete IAM role: ${response.status}`);
	}
}

// ── App & Feature Registry ──

export async function listApps(): Promise<AppRegistryEntry[]> {
	const serviceFetch = await sf();
	const response = await serviceFetch('/v1/iam/apps', { method: 'GET' });
	if (response.status !== 200) {
		throw new Error(`Failed to fetch app registry: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && Array.isArray(result.data)) {
		return (result.data as Array<Record<string, unknown>>).map(mapAppRow);
	}
	throw new Error('Unexpected response from GET /v1/iam/apps');
}

export async function listFeatures(appId?: string): Promise<FeatureRegistryEntry[]> {
	const serviceFetch = await sf();
	const url = appId
		? `/v1/iam/features?app_id=${encodeURIComponent(appId)}`
		: '/v1/iam/features';
	const response = await serviceFetch(url, { method: 'GET' });
	if (response.status !== 200) {
		throw new Error(`Failed to fetch feature registry: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && Array.isArray(result.data)) {
		return (result.data as Array<Record<string, unknown>>).map(mapFeatureRow);
	}
	throw new Error('Unexpected response from GET /v1/iam/features');
}

// ── Permission Map (ADR-033) ──

export async function getUserPermissionMap(userId: string): Promise<PermissionMap> {
	const serviceFetch = await sf();
	const response = await serviceFetch(
		`/v1/iam/permissions?user_id=${encodeURIComponent(userId)}`,
		{ method: 'GET' },
	);
	if (response.status !== 200) {
		throw new Error(`Failed to fetch permission map: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && Array.isArray(result.data)) {
		return (result.data as Array<Record<string, unknown>>).map(mapPermissionGrant);
	}
	throw new Error('Unexpected response from GET /v1/iam/permissions');
}

export async function setUserPermissions(payload: SetUserPermissionsPayload): Promise<void> {
	const serviceFetch = await sf();
	const response = await serviceFetch('/v1/iam/permissions', {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			user_id: payload.userId,
			permissions: payload.permissions.map(p => ({
				app_id: p.appId,
				feature_id: p.featureId ?? null,
				action: p.action,
				granted: p.granted,
				valid_from: p.validFrom ?? null,
				valid_until: p.validUntil ?? null,
				max_invocations: p.maxInvocations ?? null,
				rate_limit_per_hour: p.rateLimitPerHour ?? null,
				reason: p.reason ?? null,
			})),
		}),
	});
	if (response.status !== 200) {
		throw new Error(`Failed to set user permissions: ${response.status}`);
	}
}

// ── Role Permissions ──

export async function getRolePermissions(roleId: string): Promise<RolePermission[]> {
	const serviceFetch = await sf();
	const response = await serviceFetch(
		`/v1/iam/role-permissions/${encodeURIComponent(roleId)}`,
		{ method: 'GET' },
	);
	if (response.status !== 200) {
		throw new Error(`Failed to fetch role permissions: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && Array.isArray(result.data)) {
		return (result.data as Array<Record<string, unknown>>).map(mapRolePermission);
	}
	throw new Error('Unexpected response from GET /v1/iam/role-permissions');
}

export async function setRolePermissions(payload: SetRolePermissionsPayload): Promise<void> {
	const serviceFetch = await sf();
	// Wire key is `entries` (SetRolePermissionsRequest) — REQUIRED, no default.
	// Was sent as `permissions` (not a field on that schema at all), which
	// omitted the required key entirely; every call would have 422'd.
	const response = await serviceFetch(
		`/v1/iam/role-permissions/${encodeURIComponent(payload.roleId)}`,
		{
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				entries: payload.permissions.map(p => ({
					app_id: p.appId,
					feature_id: p.featureId ?? null,
					action: p.action,
					granted: p.granted,
				})),
			}),
		},
	);
	if (response.status !== 200) {
		throw new Error(`Failed to set role permissions: ${response.status}`);
	}
}

// ── Access Levels (Layer 2 — Odoo pattern) ──

export async function listAccessLevels(appId?: string): Promise<AppAccessLevel[]> {
	const serviceFetch = await sf();
	const url = appId
		? `/v1/iam/access-levels?app_id=${encodeURIComponent(appId)}`
		: '/v1/iam/access-levels';
	const response = await serviceFetch(url, { method: 'GET' });
	if (response.status !== 200) {
		throw new Error(`Failed to fetch access levels: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && Array.isArray(result.data)) {
		return (result.data as Array<Record<string, unknown>>).map(mapAccessLevel);
	}
	throw new Error('Unexpected response from GET /v1/iam/access-levels');
}

export async function getRoleLevels(roleId: string): Promise<RoleAppLevel[]> {
	const serviceFetch = await sf();
	const response = await serviceFetch(
		`/v1/iam/role-levels/${encodeURIComponent(roleId)}`,
		{ method: 'GET' },
	);
	if (response.status !== 200) {
		throw new Error(`Failed to fetch role levels: ${response.status}`);
	}
	const result = response.data as Record<string, unknown>;
	if (result.success && Array.isArray(result.data)) {
		return (result.data as Array<Record<string, unknown>>).map(mapRoleAppLevel);
	}
	throw new Error('Unexpected response from GET /v1/iam/role-levels');
}

export async function setRoleLevels(payload: SetRoleLevelsPayload): Promise<void> {
	const serviceFetch = await sf();
	const response = await serviceFetch(
		`/v1/iam/role-levels/${encodeURIComponent(payload.roleId)}`,
		{
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				levels: payload.levels.map(l => ({
					app_id: l.appId,
					level_key: l.levelKey,
				})),
			}),
		},
	);
	if (response.status !== 200) {
		throw new Error(`Failed to set role levels: ${response.status}`);
	}
}

